import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import Button from "../../../components/Button";
import EntityPicker from "../../../components/EntityPicker";
import FormField from "../../../components/FormField";
import TextArea from "../../../components/TextArea";
import TextInput from "../../../components/TextInput";
import { useArtistsList } from "../../../../hooks/useArtistsList";
import { useCreateArtist } from "../../../../hooks/useCreateArtist";
import { useUpdateParcours } from "../../../../hooks/useUpdateParcours";
import { parcoursResponseToDto } from "../toDto";
import InlineArtistEditor from "./InlineArtistEditor";
import type { ArtistMiniResponse, ParcoursResponse } from "../../../../api/parcoursApi";

const EMPTY_NEW = { name: "", description: "", photoLink: "" };

export default function ParcoursArtistsTab() {
  const { id = "" } = useParams<{ id: string }>();
  const parcours = useOutletContext<ParcoursResponse>();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_NEW);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const list = useArtistsList({ search, page: 0, size: 30, sort: "name,asc" });
  const create = useCreateArtist();
  const update = useUpdateParcours(id);

  const addArtist = (artistId: string) => {
    const next = parcoursResponseToDto(parcours);
    next.artistIds = Array.from(new Set([...(next.artistIds ?? []), artistId]));
    update.mutate(next);
  };

  const removeArtist = (artistId: string) => {
    const next = parcoursResponseToDto(parcours);
    next.artistIds = (next.artistIds ?? []).filter((aid) => aid !== artistId);
    if (expandedId === artistId) setExpandedId(null);
    update.mutate(next);
  };

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    create.mutate(
      {
        name: draft.name.trim(),
        description: draft.description || undefined,
        photoLink: draft.photoLink || undefined,
      },
      {
        onSuccess: (created) => {
          addArtist(created.id);
          setDraft(EMPTY_NEW);
          setCreating(false);
        },
      },
    );
  };

  const pickerOptions =
    list.data?.content.map((a) => ({
      id: a.id,
      label: a.name,
      sublabel: a.description ? a.description.slice(0, 60) : "",
    })) ?? [];
  const attached = parcours.artists ?? [];
  const assignedIds = attached.map((a) => a.id);

  return (
    <div className="bo-stack">
      <div className="bo-card">
        <h3 style={{ margin: 0, marginBottom: "var(--sp-4)", fontFamily: "var(--font-display)", fontSize: 18 }}>
          Artistes du parcours ({attached.length})
        </h3>
        {attached.length === 0 ? (
          <p style={{ color: "var(--ink-mute)", fontSize: 13, margin: 0 }}>
            Aucun artiste pour le moment. Recherchez-en un ou créez-en un nouveau ci-dessous.
          </p>
        ) : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--sp-2)",
            }}
          >
            {attached.map((a) => (
              <ArtistRow
                key={a.id}
                artist={a}
                expanded={expandedId === a.id}
                onToggle={() => setExpandedId(expandedId === a.id ? null : a.id)}
                onRemove={() => removeArtist(a.id)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="bo-card">
        <h3 style={{ margin: 0, marginBottom: "var(--sp-3)", fontFamily: "var(--font-display)", fontSize: 18 }}>
          Ajouter un artiste
        </h3>

        <EntityPicker
          placeholder="Rechercher un artiste par nom…"
          options={pickerOptions}
          excludeIds={assignedIds}
          onSearchChange={setSearch}
          onPick={(o) => addArtist(o.id)}
          loading={list.isLoading}
        />

        {!creating && (
          <div style={{ marginTop: "var(--sp-4)" }}>
            <Button type="button" variant="secondary" onClick={() => setCreating(true)}>
              + Créer un nouvel artiste
            </Button>
          </div>
        )}

        {creating && (
          <form
            onSubmit={submitNew}
            style={{ marginTop: "var(--sp-4)", paddingTop: "var(--sp-4)", borderTop: "1px solid var(--line-soft)" }}
            className="bo-stack"
          >
            <FormField label="Nom" required>
              <TextInput
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                required
                autoFocus
              />
            </FormField>
            <FormField label="Description">
              <TextArea
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={3}
              />
            </FormField>
            <FormField label="Lien vers la photo">
              <TextInput
                type="url"
                value={draft.photoLink}
                onChange={(e) => setDraft({ ...draft, photoLink: e.target.value })}
                placeholder="https://…"
              />
            </FormField>
            <div style={{ display: "flex", gap: "var(--sp-2)", justifyContent: "flex-end" }}>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" loading={create.isPending}>
                Créer et ajouter au parcours
              </Button>
            </div>
          </form>
        )}

        {update.isError && (
          <p className="bo-error-text" style={{ marginTop: "var(--sp-3)" }}>
            Erreur : {(update.error as Error)?.message}
          </p>
        )}
      </div>
    </div>
  );
}

interface ArtistRowProps {
  artist: ArtistMiniResponse;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
}

function ArtistRow({ artist, expanded, onToggle, onRemove }: ArtistRowProps) {
  return (
    <li
      style={{
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--radius-s)",
        background: "var(--paper)",
        padding: "var(--sp-3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-3)" }}>
        {artist.photoLink ? (
          <img
            src={artist.photoLink}
            alt=""
            style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "var(--paper-2)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-display)",
              fontSize: 14,
              color: "var(--ink-mute)",
            }}
          >
            {artist.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: "var(--ink)" }}>{artist.name}</div>
          {artist.description && (
            <div
              style={{
                fontSize: 12,
                color: "var(--ink-mute)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {artist.description}
            </div>
          )}
        </div>
        <div className="bo-row-end">
          <Button variant="ghost" onClick={onToggle}>
            {expanded ? "Replier" : "Éditer"}
          </Button>
          <Button variant="ghost" onClick={onRemove}>
            Retirer
          </Button>
        </div>
      </div>
      {expanded && <InlineArtistEditor artistId={artist.id} onClose={onToggle} />}
    </li>
  );
}
